import { buildPracticeLaunchUrl } from './practiceAssignments';
import { formatIeltsBand, ieltsBandFromAttempt, IELTS_PRACTICE_TYPE } from './ieltsSpeaking';

export const PRACTICE_LABELS = Object.freeze({
  dictation: 'Dictation Loop',
  meaning_sprint: '7-Second Meaning',
  sudden_sprint: 'Lightning Challenge',
  pronunciation: 'Pronunciation Practice',
  adaptive_reading: 'Adaptive Reading',
  ielts_speaking: 'IELTS Speaking Practice',
});

const hasValue = (value) => value !== undefined && value !== null && value !== '';

export const firstDefined = (...values) => values.find(hasValue);

const numberOrNull = (value) => {
  if (!hasValue(value)) return null;
  const number = Number(value);
  return Number.isFinite(number) ? number : null;
};

const integerOr = (value, fallback) => {
  const number = numberOrNull(value);
  return number == null ? fallback : Math.max(0, Math.round(number));
};

export const normalizeAccuracy = (value) => {
  const number = numberOrNull(value);
  if (number == null) return null;
  return Math.max(0, Math.min(1, number > 1 ? number / 100 : number));
};

export const accuracyPercent = (value) => {
  const normalized = normalizeAccuracy(value);
  return normalized == null ? null : Math.round(normalized * 100);
};

export const attemptTimestamp = (attempt) => (
  attempt?.completed_at
  || attempt?.finished_at
  || attempt?.submitted_at
  || attempt?.started_at
  || attempt?.created_at
  || attempt?.ts
  || ''
);

export const normalizeAttempt = (raw = {}) => {
  const metrics = raw.metrics || raw.summary || {};
  const accuracy = firstDefined(
    raw.accuracy,
    raw.score_percent,
    metrics.accuracy,
    metrics.score_percent,
  );
  return {
    ...raw,
    id: firstDefined(raw.id, raw._id, raw.attempt_id),
    attempt_no: integerOr(firstDefined(raw.attempt_no, raw.attempt_number, raw.attempt), 0),
    status: raw.status || (raw.completed_at ? 'completed' : 'submitted'),
    accuracy: normalizeAccuracy(accuracy),
    score: firstDefined(raw.score, metrics.score),
    predicted_overall: raw.practice_type === IELTS_PRACTICE_TYPE
      ? ieltsBandFromAttempt(raw)
      : null,
    max_score: firstDefined(raw.max_score, metrics.max_score),
    duration_ms: firstDefined(raw.duration_ms, raw.duration, metrics.duration_ms),
    metrics,
    detail_ref: raw.detail_ref || null,
  };
};

export const practiceScoreLabel = (attempt = {}, practiceType = '') => {
  if (practiceType === IELTS_PRACTICE_TYPE || attempt.practice_type === IELTS_PRACTICE_TYPE) {
    const band = ieltsBandFromAttempt(attempt);
    return band == null ? '' : formatIeltsBand(band);
  }
  const percent = accuracyPercent(attempt.accuracy);
  return percent == null ? '' : `${percent}%`;
};

const rawAttemptsFrom = (payload = {}) => {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload.attempts)) return payload.attempts;
  if (Array.isArray(payload.items)) return payload.items;
  if (Array.isArray(payload.history)) return payload.history;
  if (Array.isArray(payload.report?.attempts)) return payload.report.attempts;
  return [];
};

export const practiceAssignmentFromPayload = (payload = {}) => {
  const root = payload && typeof payload === 'object' ? payload : {};
  const data = root.data && typeof root.data === 'object' ? root.data : {};
  const practice = root.practice && typeof root.practice === 'object' ? root.practice : null;
  const dataPractice = data.practice && typeof data.practice === 'object' ? data.practice : null;
  const isAssignment = (value) => Boolean(
    value
      && typeof value === 'object'
      && (value.title != null || value.config != null || value.source != null)
      && (value.id != null || value._id != null || value.practice_type != null),
  );
  return root.assignment
    || data.assignment
    || practice?.assignment
    || dataPractice?.assignment
    || (isAssignment(practice) ? practice : null)
    || (isAssignment(dataPractice) ? dataPractice : null)
    || (isAssignment(root) ? root : null);
};

const hasPaginationMetadata = (payload = {}) => {
  const pagination = payload.pagination || payload.meta?.pagination || payload.report?.pagination;
  if (pagination && typeof pagination === 'object' && Object.keys(pagination).length > 0) return true;
  return ['page', 'page_size', 'pageSize', 'total', 'total_count', 'has_next', 'hasNext']
    .some((key) => hasValue(payload[key]));
};

const paginationFrom = (payload, count, requestedPage, requestedPageSize) => {
  const pagination = payload.pagination || payload.meta?.pagination || payload.report?.pagination || {};
  const page = integerOr(firstDefined(pagination.page, payload.page), requestedPage) || 1;
  const pageSize = integerOr(
    firstDefined(pagination.page_size, pagination.pageSize, payload.page_size, payload.pageSize),
    requestedPageSize,
  ) || requestedPageSize;
  const total = integerOr(
    firstDefined(pagination.total, payload.total, payload.total_count, payload.report?.total),
    count,
  );
  const totalPages = Math.max(1, Math.ceil(total / Math.max(1, pageSize)));
  const explicitHasNext = firstDefined(pagination.has_next, pagination.hasNext, payload.has_next, payload.hasNext);
  return {
    page,
    page_size: pageSize,
    total,
    total_pages: integerOr(firstDefined(pagination.total_pages, pagination.totalPages), totalPages) || totalPages,
    has_next: hasValue(explicitHasNext) ? Boolean(explicitHasNext) : page < totalPages,
    has_previous: page > 1,
  };
};

export const normalizeAttemptPage = (payload, requestedPage = 1, requestedPageSize = 10) => {
  const root = payload || {};
  const reportPayload = root.report || (root.summary ? root : null);
  const report = reportPayload ? normalizePracticeReport(reportPayload) : null;
  const allAttempts = rawAttemptsFrom(root).map(normalizeAttempt);
  const pagination = paginationFrom(root, allAttempts.length, requestedPage, requestedPageSize);
  const attempts = hasPaginationMetadata(root)
    ? allAttempts
    : allAttempts.slice(
      (pagination.page - 1) * pagination.page_size,
      pagination.page * pagination.page_size,
  );
  return {
    assignment: practiceAssignmentFromPayload(root) || report?.assignment || null,
    attempts,
    pagination,
    report,
  };
};

export const normalizePracticeReport = (payload) => {
  const report = payload?.report || payload || {};
  const summary = report.summary && typeof report.summary === 'object' ? report.summary : {};
  const firstAttempt = report.first_attempt
    || report.first_attempt_aggregates
    || report.first_attempt_summary
    || summary.first_attempt_only
    || {};
  const sampleSize = integerOr(firstDefined(
    report.first_attempt_sample_size,
    report.first_attempt_sample,
    firstAttempt.sample_size,
    firstAttempt.sample,
    firstAttempt.n,
    summary.first_attempt_sample_size,
  ), 0);
  const normalizedFirstAttempt = {
    ...firstAttempt,
    sample_size: firstDefined(firstAttempt.sample_size, sampleSize),
    accuracy: firstDefined(firstAttempt.accuracy, firstAttempt.average_accuracy),
    score_percent: firstDefined(firstAttempt.score_percent, firstAttempt.average_score_percent),
    predicted_overall: firstDefined(
      firstAttempt.predicted_overall,
      firstAttempt.predicted_band,
      firstAttempt.average_predicted_overall,
      firstAttempt.average_overall_predicted,
    ),
    dimensions: firstAttempt.dimensions
      || firstAttempt.band_scores
      || firstAttempt.dimension_scores
      || firstAttempt.average_dimension_scores
      || {},
  };
  const audienceCount = integerOr(firstDefined(
    report.audience_count,
    report.total_students,
    report.audience?.count,
    report.audience_snapshot?.student_ids?.length,
    summary.audience_count,
    summary.target_count,
  ), 0);
  const completedCount = integerOr(firstDefined(
    report.completed_count,
    report.completed_students,
    report.completion?.completed,
    summary.completed_count,
  ), 0);
  return {
    ...report,
    assignment: report.assignment || payload?.assignment || null,
    completion_rate: firstDefined(
      report.completion_rate,
      report.completion?.rate,
      summary.completion_rate,
      audienceCount > 0 ? completedCount / audienceCount : null,
    ),
    completed_count: completedCount,
    audience_count: audienceCount,
    first_attempt: normalizedFirstAttempt,
    first_attempt_sample_size: sampleSize,
    attempts: Array.isArray(report.attempts) ? report.attempts.map(normalizeAttempt) : [],
  };
};

export const normalizeIeltsTeacherReport = (payload = {}) => {
  const report = normalizePracticeReport(payload);
  const first = report.first_attempt || {};
  const predictedOverall = ieltsBandFromAttempt({
    ...first,
    practice_type: IELTS_PRACTICE_TYPE,
    metrics: first.metrics || {},
  });
  return {
    ...report,
    practice_type: report.practice_type || report.assignment?.practice_type || IELTS_PRACTICE_TYPE,
    first_attempt: {
      ...first,
      predicted_overall: predictedOverall,
      dimensions: first.dimensions || {},
    },
  };
};

export const normalizeAttemptDetail = (payload) => {
  const root = payload || {};
  const attempt = normalizeAttempt(root.attempt || root.data?.attempt || root);
  const assignment = root.assignment || root.data?.assignment || attempt.assignment || null;
  const details = root.details
    || root.data?.details
    || root.detail
    || root.data?.detail
    || attempt.details
    || attempt.detail
    || {};
  return {
    ...root,
    assignment,
    attempt,
    details,
  };
};

export const getPracticeCardActions = (item) => {
  const assignmentId = item?.assignment?.id || item?.assignment_id || item?.id;
  return {
    assignmentId,
    canPracticeAgain: Boolean(assignmentId) && item?.status !== 'upcoming',
    canViewAllAttempts: Boolean(assignmentId) && item?.status === 'completed',
  };
};

export const buildPracticeHistoryPath = (assignmentId, viewer = 'student') => (
  viewer === 'admin'
    ? `/admin/practice/${encodeURIComponent(assignmentId)}/report`
    : `/review/practice/${encodeURIComponent(assignmentId)}`
);

export const buildPracticeAttemptPath = (attemptId, viewer = 'student') => (
  viewer === 'admin'
    ? `/admin/practice/attempt/${encodeURIComponent(attemptId)}`
    : `/review/practice/attempt/${encodeURIComponent(attemptId)}`
);

export const studentOverviewMetrics = (stats = {}) => ([
  {
    key: 'completion_rate',
    label: 'Completion:',
    value: firstDefined(stats.completion_rate, 0),
  },
]);

const authHeaders = (json = false) => ({
  ...(json ? { 'Content-Type': 'application/json' } : {}),
  Authorization: `Bearer ${localStorage.getItem('token')}`,
});

export const fetchPracticeJson = async (url, options = {}) => {
  const response = await fetch(url, {
    ...options,
    headers: { ...authHeaders(Boolean(options.body)), ...(options.headers || {}) },
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    const error = new Error(data.message || data.error || 'The practice result could not be loaded.');
    error.status = response.status;
    error.code = data.code;
    throw error;
  }
  return data;
};

export const fetchPracticeAssignment = (assignmentId, viewer = 'student') => fetchPracticeJson(
  viewer === 'admin'
    ? `/api/admin/practice-assignments/${encodeURIComponent(assignmentId)}`
    : `/api/student/practice-assignments/${encodeURIComponent(assignmentId)}`,
);

export const fetchTeacherPracticeAssignments = async (query = '') => {
  const suffix = query ? `?${query.replace(/^\?/, '')}` : '';
  const data = await fetchPracticeJson(`/api/admin/practice-assignments${suffix}`);
  return Array.isArray(data.assignments) ? data.assignments : [];
};

export const fetchPracticeAttemptHistory = async ({
  assignmentId,
  viewer = 'student',
  studentId = '',
  page = 1,
  pageSize = 10,
}) => {
  const query = new URLSearchParams({ page: String(page), page_size: String(pageSize) });
  if (viewer === 'admin') {
    if (studentId) {
      query.set('student_id', String(studentId));
      return normalizeAttemptPage(
        await fetchPracticeJson(
          `/api/admin/practice-assignments/${encodeURIComponent(assignmentId)}/attempts?${query.toString()}`,
        ),
        page,
        pageSize,
      );
    }
    const data = await fetchPracticeJson(
      `/api/admin/practice-assignments/${encodeURIComponent(assignmentId)}/report?${query.toString()}`,
    );
    const normalized = normalizeAttemptPage(data, page, pageSize);
    if (normalized.attempts.length || normalized.report?.attempts?.length) return normalized;
    try {
      const attempts = await fetchPracticeJson(
        `/api/admin/practice-assignments/${encodeURIComponent(assignmentId)}/attempts?${query.toString()}`,
      );
      const attemptPage = normalizeAttemptPage(attempts, page, pageSize);
      return {
        ...normalized,
        assignment: normalized.assignment || attemptPage.assignment,
        attempts: attemptPage.attempts,
        pagination: attemptPage.pagination,
      };
    } catch (_) {
      return normalized;
    }
  }
  return normalizeAttemptPage(
    await fetchPracticeJson(`/api/student/practice-assignments/${encodeURIComponent(assignmentId)}/attempts?${query.toString()}`),
    page,
    pageSize,
  );
};

export const fetchPracticeAttemptDetail = (attemptId, viewer = 'student') => fetchPracticeJson(
  viewer === 'admin'
    ? `/api/admin/practice-attempts/${encodeURIComponent(attemptId)}`
    : `/api/student/practice-attempts/${encodeURIComponent(attemptId)}`,
).then(normalizeAttemptDetail);

export const startPracticeAgain = async (assignmentId) => {
  const data = await fetchPracticeJson(
    `/api/student/practice-assignments/${encodeURIComponent(assignmentId)}/start`,
    { method: 'POST', body: JSON.stringify({}) },
  );
  return buildPracticeLaunchUrl(data.launch);
};
